import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../api/supabase'
import type { PricingCategory, PricingUnit } from './types'
import { PRICING_UNIT_OPTIONS } from './types'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

type FormState = Omit<PricingCategory, 'id'> & { id?: string }

function emptyForm(): FormState {
  return { code: '', name_en: '', name_zh: '', default_pricing_unit: 'flat', description: '', display_order: 0, is_active: true }
}

export default function CategoriesTab() {
  const [rows, setRows] = useState<PricingCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<FormState | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('pricing_categories').select('*').order('display_order')
    setRows((data as PricingCategory[]) || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openAdd = () => { setForm(emptyForm()); setError(null); setModal(true) }
  const openEdit = (r: PricingCategory) => { setForm({ ...r }); setError(null); setModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form) return
    if (!form.code.trim() || !form.name_en.trim()) { setError('Code and English name are required.'); return }
    setSaving(true); setError(null)
    try {
      const { id, ...rest } = form
      const { error: err } = id
        ? await supabase.from('pricing_categories').update(rest).eq('id', id)
        : await supabase.from('pricing_categories').insert(rest)
      if (err) throw err
      setModal(false)
      load()
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (r: PricingCategory) => {
    await supabase.from('pricing_categories').update({ is_active: !r.is_active }).eq('id', r.id)
    load()
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Top-level service categories -- the first axis every rate card, lane, and charge is organized under.</p>
        <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 whitespace-nowrap ml-4">
          + New Category
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {['Code', 'Name (EN)', 'Name (ZH)', 'Default Unit', 'Order', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">{r.code}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{r.name_en}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.name_zh || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">{r.default_pricing_unit}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.display_order}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-3">
                    <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                    <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                      {r.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No categories found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{form.id ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code *</label>
                <input className={inputCls} value={form.code} onChange={e => setForm(f => f && ({ ...f, code: e.target.value.toUpperCase() }))} maxLength={30} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name (EN) *</label>
                  <input className={inputCls} value={form.name_en} onChange={e => setForm(f => f && ({ ...f, name_en: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name (ZH)</label>
                  <input className={inputCls} value={form.name_zh || ''} onChange={e => setForm(f => f && ({ ...f, name_zh: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Pricing Unit</label>
                <select className={inputCls} value={form.default_pricing_unit} onChange={e => setForm(f => f && ({ ...f, default_pricing_unit: e.target.value as PricingUnit }))}>
                  {PRICING_UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea className={inputCls} rows={2} value={form.description || ''} onChange={e => setForm(f => f && ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Order</label>
                <input type="number" className={inputCls} value={form.display_order} onChange={e => setForm(f => f && ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => f && ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
